import { getCountryByAbbreviation, getCountry } from "country-currency-map";
import dayjs from "dayjs";
import { getCountryCallingCode } from "libphonenumber-js/min";
import type { CountryCode } from "libphonenumber-js/types";
import { SettlementPayoutFeeRule } from "../types/walletTypes";


export const resolveCNYLinkRoute = (link, hasFundingWallet, enableChinaPayoutV2 = false) => {
  if (link.route) return link.route;

  if (!hasFundingWallet && link.key === "china-payout") {
    return "/dashboard/guide/china-payout";
  }

  if (link.key === "china-payout" && enableChinaPayoutV2) {
    return "/wallets/cny/payout/v2";
  }

  return "/wallets/cny/payout";
};


export const capitalizeCurrencyCode = (currency: string | null | undefined | any): string | undefined => {
  if (typeof currency !== "string") return undefined;
  return currency.toUpperCase();
};

const OBSOLETE_ISO_CURRENCY_CODES: Record<string, { code: string; name: string }> = {
  GHC: { code: "GHS", name: "Ghanaian Cedi" },
};

export const normalizeCurrencyCode = (currency: unknown): string | undefined => {
  const capitalized = capitalizeCurrencyCode(currency);
  if (!capitalized) {
    return capitalized;
  }

  return OBSOLETE_ISO_CURRENCY_CODES[capitalized]?.code ?? capitalized;
};

export const getNormalizedCurrencyName = (currency: unknown): string | undefined => {
  const capitalized = capitalizeCurrencyCode(currency);
  if (!capitalized) {
    return undefined;
  }

  const obsolete = OBSOLETE_ISO_CURRENCY_CODES[capitalized];
  if (obsolete) {
    return obsolete.name;
  }

  return Object.values(OBSOLETE_ISO_CURRENCY_CODES).find(
    (entry) => entry.code === capitalized,
  )?.name;
};

export const withNormalizedCurrency = <T extends object>(payload: T): T => {
  if (!payload || typeof payload !== "object" || !("currency" in payload)) {
    return payload;
  }

  const currentCurrency = (payload as { currency?: unknown }).currency;
  if (currentCurrency === "") {
    return payload;
  }

  return {
    ...payload,
    currency: normalizeCurrencyCode(currentCurrency) ?? currentCurrency,
  };
};

export const enhancedGetCountryByAbbreviation = (abbrevition: string) => {
  if (abbrevition === "GB") {
    return "United Kingdom";
  }
  return getCountryByAbbreviation(abbrevition);
};

export const standardizeBankCode = (value) => {
  if (typeof value !== "string") return undefined;

  const positionOfUnderscore = value.indexOf("_");
  if (positionOfUnderscore === -1) return value;

  return value.substring(0, positionOfUnderscore);
};

export const extractBaseBankCode = (modifiedBankCode: string | undefined) => {
  if (!modifiedBankCode) {
      return '';
  }
  const underscoreIndex = modifiedBankCode.indexOf("_");
  return underscoreIndex !== -1
      ? modifiedBankCode.substring(0, underscoreIndex)
      : modifiedBankCode;
}

export const isWithinWATBusinessHours = () => {
  const now = new Date();

  const utcOffset = now.getTimezoneOffset() * 60000;
  const watOffset = 1 * 60 * 60000;
  const watTime = new Date(now.getTime() + utcOffset + watOffset);

  const hours = watTime.getHours(); 

  return hours >= 6 && hours < 18;
}

export const calculateProcessingFee = (amount: number, feePercentage: number) => {
  return (amount * feePercentage) / 100;
};

const SETTLEMENT_PAYOUT_FEE_RULES: Record<string, SettlementPayoutFeeRule> = {
  "ghsPayout:MOBILE_MONEY": { type: "percentage", rate: 2 },
  "ghsPayout:BANK_ACCOUNT": { type: "flat", amount: 15 },
  "ngnPayout": { type: "flat", amount: 53.75 },
};

export function computeSettlementPayoutFee(
  amount = 0,
  payoutMethod?: string,
  paymentMethod?: string
): number {
  const rule =
    (paymentMethod && SETTLEMENT_PAYOUT_FEE_RULES[`${payoutMethod}:${paymentMethod}`]) ||
    SETTLEMENT_PAYOUT_FEE_RULES[payoutMethod] ||
    { type: "flat", amount: 0 };

    return rule.type === "percentage"
    ? calculateProcessingFee(amount, rule.rate)
    : rule.amount;
}

const EXPORT_PERMISSION_MAP: Record<string, string> = {
  collections: "EXPORT_COLLECTION_STATEMENT",
  wallet: "EXPORT_WALLET_STATEMENT",
  transactions: "EXPORT_TRANSACTION_STATEMENT",
};

export const canUserExport = (location: string, permissions: any): boolean => {
  const neededPermission = EXPORT_PERMISSION_MAP[location];
  const hasLocationPermission = permissions?.[location]?.includes(neededPermission);
  const hasGlobalPermission = permissions?.includes?.(neededPermission);
  return !!(hasLocationPermission || hasGlobalPermission);
};

export const convertToDateOnly = (
  date: string | Date | dayjs.Dayjs | null | undefined
): string | null => {
  if (date === null || date === undefined) return null;
  return dayjs(date).format("YYYY-MM-DD");
};

export const NGN_ACCOUNT_NUMBER_LENGTH = 10;
export const GHS_ACCOUNT_NUMBER_MAX_LENGTH = 13;
export const GHS_PHONE_NUMBER_ERROR_MESSAGE =
  "Must be between 10-13 digits";

export const getPhoneCallingCode = (defaultCountry?: string): string => {
  try {
    return defaultCountry
      ? String(getCountryCallingCode(defaultCountry as CountryCode))
      : "";
  } catch {
    return "";
  }
};

export const getNationalDigits = (
  value: string,
  callingCode: string,
): string => {
  const digits = value.replace(/\D/g, "");
  return callingCode && digits.startsWith(callingCode)
    ? digits.slice(callingCode.length)
    : digits;
};

export const getNationalDigitCount = (
  value: string,
  callingCode: string,
): number => getNationalDigits(value, callingCode).length;

export const getGhsNationalDigits = (value?: string): string =>
  getNationalDigits(String(value ?? ""), "233");

export const limitPhoneNationalDigits = (
  value: string | undefined,
  maxLength: number,
  callingCode = "233",
): string | undefined => {
  if (!value) return value;

  const digits = value.replace(/\D/g, "");
  const hasCallingCode = Boolean(callingCode) && digits.startsWith(callingCode);
  const national = hasCallingCode ? digits.slice(callingCode.length) : digits;
  const limitedNational = national.slice(0, maxLength);

  if (!limitedNational) {
    return hasCallingCode ? `+${callingCode}` : "";
  }

  return hasCallingCode ? `+${callingCode}${limitedNational}` : limitedNational;
};

export const validateGhsPhoneNumber = (value?: string): true | string => {
  const national = getGhsNationalDigits(value);
  if (!national.length) return true;

  if (national.length > GHS_ACCOUNT_NUMBER_MAX_LENGTH) {
    return GHS_PHONE_NUMBER_ERROR_MESSAGE;
  }

  // The country code (+233) is excluded via getGhsNationalDigits.
  const minLength = 10;
  if (national.length < minLength) {
    return GHS_PHONE_NUMBER_ERROR_MESSAGE;
  }

  return true;
};

export const sanitizeAccountNumberInput = (
  value: string,
  maxLength: number,
) => value.replace(/\D/g, "").slice(0, maxLength);

export const getAccountNumberMaxLength = (currencyCode?: string) =>
  capitalizeCurrencyCode(currencyCode) === "GHS"
    ? GHS_ACCOUNT_NUMBER_MAX_LENGTH
    : NGN_ACCOUNT_NUMBER_LENGTH;

export const getAccountNumberPattern = (currencyCode?: string) =>
  capitalizeCurrencyCode(currencyCode) === "GHS"
    ? /^\d{10,13}$/
    : /^\d{10}$/;

export const getAccountNumberErrorMessage = (currencyCode?: string) =>
  capitalizeCurrencyCode(currencyCode) === "GHS"
    ? "Account number must be between 10-13 digits"
    : "Account number must be 10 digits";

export const sanitizeDecimalAmountInput = (
  value: string | number | null | undefined,
): string => {
  const digitsAndDot = String(value ?? "").replace(/[^\d.]/g, "");
  const [integerPart = "", ...decimalParts] = digitsAndDot.split(".");

  if (decimalParts.length === 0) {
    return integerPart;
  }

  return `${integerPart}.${decimalParts.join("")}`;
};

export const parseDecimalAmount = (
  value: string | number | null | undefined,
): number | undefined => {
  const sanitized = sanitizeDecimalAmountInput(value);

  if (!sanitized || sanitized === ".") {
    return undefined;
  }

  const parsed = Number(sanitized);
  return Number.isNaN(parsed) ? undefined : parsed;
};

export const isValidMinimumAmount = (
  value: string | number | null | undefined,
  minimum: number,
): boolean => {
  const amount = Number(value);
  return Number.isFinite(amount) && amount >= minimum;
};

export const blockInvalidDecimalAmountKey = (
  event: React.KeyboardEvent<HTMLInputElement>,
) => {
  if (
    event.ctrlKey ||
    event.metaKey ||
    [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "Tab",
      "Home",
      "End",
    ].includes(event.key)
  ) {
    return;
  }

  if (/^\d$/.test(event.key)) {
    return;
  }

  if (event.key === "." && !event.currentTarget.value.includes(".")) {
    return;
  }

  event.preventDefault();
};